import { BORDER, CARD_SHADOW_HOVER, RISK_COLORS } from "@app/theme/tokens";
import { MM_TO_PTS } from "@features/expert/utils/kks";
import { useAuth } from "@laborelec/lbe-react/auth";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  Divider,
  IconButton,
  keyframes,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { getPidPdfUrl } from "../api/pidExplorer";
import type { PidDetail, PidTag } from "../types";
import { days, mw } from "../utils/format";
import { RiskChip } from "./Chips";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const FALLBACK_WIDTH = 900;

/** Ring drawn around the tag a visitor was deep-linked to, so it cannot be missed. */
const focusPulse = keyframes`
  0% { box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 5px #E53935, 0 0 0 5px rgba(229,57,53,0.55); }
  70% { box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 5px #E53935, 0 0 0 22px rgba(229,57,53,0); }
  100% { box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 5px #E53935, 0 0 0 22px rgba(229,57,53,0); }
`;

/** Sheet the drawing opens on: the tags often sit on a later page than the cover sheet. */
function firstTaggedPage(tags: PidTag[]) {
  return tags.length === 0 ? 1 : Math.min(...tags.map((tag) => tag.page ?? 1));
}

/** Only a KKS matched to a register SPOF is painted; a drawing flag stays a faint outline. */
function spofColor(tag: PidTag): string | null {
  return tag.spofs.length > 0 ? RISK_COLORS[4] : null;
}

export default function PidCanvas({
  plantId,
  pid,
  selectedTagId,
  focused,
  onSelectTag,
}: {
  plantId: string;
  pid: PidDetail;
  selectedTagId: string | null;
  focused: boolean;
  onSelectTag: (tagId: string) => void;
}) {
  const { token } = useAuth();
  const [page, setPage] = useState(() => firstTaggedPage(pid.tags));
  const [pageCount, setPageCount] = useState(pid.total_pages ?? 1);
  const [zoom, setZoom] = useState(1);
  const [pageScale, setPageScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(FALLBACK_WIDTH);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setContainerWidth((entry.target as HTMLElement).clientWidth),
    );
    observer.observe(containerRef.current!);
    return () => observer.disconnect();
  }, []);

  const file = useMemo(
    () => ({
      url: getPidPdfUrl(plantId, pid.id),
      httpHeaders: { Authorization: `Bearer ${token}` },
    }),
    [plantId, pid.id, token],
  );

  const selectedTag = pid.tags.find((tag) => tag.kks_id === selectedTagId);

  useEffect(() => {
    if (selectedTag?.page) {
      setPage(selectedTag.page);
    }
  }, [selectedTag?.page]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "center", inline: "center" });
  }, [selectedTagId, pageScale]);

  const pageTags = pid.tags.filter((tag) => (tag.page ?? 1) === page);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          p: 1,
          flexWrap: "wrap",
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {pageCount > 1 && (
          <>
            <IconButton
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Page {page} / {pageCount}
            </Typography>
            <IconButton
              size="small"
              disabled={page >= pageCount}
              onClick={() => setPage(page + 1)}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </>
        )}
        <IconButton size="small" onClick={() => setZoom(zoom / 1.25)}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setZoom(zoom * 1.25)}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: "auto",
          // Without a reserved gutter the page would resize as the scrollbar appears, which
          // makes it appear again on the next render: the drawing then flickers forever.
          scrollbarGutter: "stable",
          bgcolor: "#EEF1F5",
          p: 2,
        }}
      >
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Document
            file={file}
            onLoadSuccess={({ numPages }) => setPageCount(numPages)}
          >
            <Page
              pageNumber={page}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={Math.max(containerWidth - 48, 1) * zoom}
              onRenderSuccess={({ width, originalWidth }) =>
                setPageScale(width / originalWidth)
              }
            />
          </Document>

          {pageTags.map((tag) => (
            <TagBox
              key={tag.kks_id}
              ref={tag.kks_id === selectedTagId ? selectedRef : undefined}
              tag={tag}
              color={spofColor(tag)}
              selected={tag.kks_id === selectedTagId}
              focused={focused && tag.kks_id === selectedTagId}
              scale={MM_TO_PTS * pageScale}
              onClick={() => onSelectTag(tag.kks_id)}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

function TagBox({
  ref,
  tag,
  color,
  selected,
  focused,
  scale,
  onClick,
}: {
  ref?: React.Ref<HTMLDivElement>;
  tag: PidTag;
  color: string | null;
  selected: boolean;
  focused: boolean;
  scale: number;
  onClick: () => void;
}) {
  return (
    <Tooltip
      title={<TagSummary tag={tag} color={color} />}
      placement="top"
      arrow
      enterDelay={250}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            border: `1px solid ${BORDER}`,
            boxShadow: CARD_SHADOW_HOVER,
            maxWidth: 320,
            p: 1.5,
          },
        },
        arrow: { sx: { color: "background.paper" } },
      }}
    >
      <Box
        ref={ref}
        onClick={onClick}
        sx={{
          position: "absolute",
          left: tag.bbox.x0 * scale,
          top: tag.bbox.y0 * scale,
          width: (tag.bbox.x1 - tag.bbox.x0) * scale,
          height: (tag.bbox.y1 - tag.bbox.y0) * scale,
          cursor: "pointer",
          borderRadius: 0.5,
          border: color ? `2px solid ${color}` : "1px dashed #A9B2BF",
          bgcolor: color ? `${color}33` : "transparent",
          boxShadow: selected ? `0 0 0 3px ${color ?? "#009DE0"}66` : "none",
          ...(focused && { animation: `${focusPulse} 1.4s ease-out infinite` }),
        }}
      />
    </Tooltip>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
}

/** What a tag carries without having to click it: KKS, register match, severity. */
function TagSummary({ tag, color }: { tag: PidTag; color: string | null }) {
  const spof = tag.spofs[0];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: color ?? "#A9B2BF",
          }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {tag.text ?? tag.kks_id}
        </Typography>
      </Box>

      {tag.component && (
        <Typography variant="caption" color="text.secondary">
          {tag.component}
        </Typography>
      )}

      <Divider />

      {!spof ? (
        <Typography variant="caption" color="text.secondary">
          No SPOF of the register is linked to this KKS.
        </Typography>
      ) : (
        <>
          <Row label="SPOF">
            {tag.spofs.map((entry) => entry.spof_id).join(", ")}
          </Row>
          {spof.failure_description_short && (
            <Typography variant="caption" color="text.secondary">
              {spof.failure_description_short}
            </Typography>
          )}
          <Row label="Risk">
            <RiskChip level={tag.risk_category} />
          </Row>
          {tag.residual_risk_category && (
            <Row label="Residual risk">
              <RiskChip level={tag.residual_risk_category} />
            </Row>
          )}
          {tag.power_loss_mw !== null && (
            <Row label="Power loss">{mw(tag.power_loss_mw)} MW</Row>
          )}
          {spof.restoration_time_days !== null && (
            <Row label="Restoration">{days(spof.restoration_time_days)}</Row>
          )}
          <Row label="Mitigations">
            {tag.mitigation_count || "none proposed"}
          </Row>
        </>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
        Click to open the full record.
      </Typography>
    </Box>
  );
}
